import type { FileType } from '@unifyapps/defs/blocks/FileUpload/types';
import { extractExtensionFromPath } from '@unifyapps/network/asset-cache/utils';

// MIME → extension. Narrow on purpose: most uploads land as
// `application/octet-stream` (no info), so this only catches the
// minority of typed uploads. Kept aligned with the build-time
// prefetch's `MIME_TO_EXTENSION` so build + runtime stay consistent.
const MIME_TO_EXTENSION: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'application/json': 'json',
  'application/pdf': 'pdf',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
  'audio/mpeg': 'mp3',
  'audio/mp4': 'm4a',
  'audio/wav': 'wav',
  'font/ttf': 'ttf',
  'font/otf': 'otf',
  'font/woff': 'woff',
  'font/woff2': 'woff2',
};

/**
 * Single source of truth for deriving a file's extension from an
 * UnifyApps upload metadata blob. Used by every block's
 * `*AssetExtractor` so the resolved `fileType` is uniform across:
 *
 *   - Build-time prefetch (`prepareClient`)
 *   - Post-login prefetch (`prefetchAssetsFromInterfaceEntities`)
 *   - Runtime cache lookup (`AssetCacheManager`)
 *
 * Priority:
 *   1. Explicit `uploadDetails.fileType` (set by the upload pipeline).
 *   2. Explicit `uploadDetails.extension`.
 *   3. Extension parsed from `uploadDetails.name` (original filename).
 *   4. Extension parsed from `uploadDetails.url` (often a download
 *      endpoint that drops the extension — last resort).
 *   5. MIME → extension lookup. Last because `application/octet-stream`
 *      is the dominant mimeType for binary uploads and tells us nothing.
 *
 * Returns `undefined` only when nothing in the metadata yields an
 * extension — callers can apply per-block defaults (Lottie → 'json',
 * Icon → 'svg', etc.) or pass undefined and let the cache fall back to
 * 'bin'.
 */
export function resolveFileTypeFromUploadDetails(uploadDetails?: FileType): string | undefined {
  if (!uploadDetails) return undefined;

  const explicit = toLowerCase(uploadDetails.fileType);
  if (explicit) return explicit;

  const explicitExt = toLowerCase(uploadDetails.extension);
  if (explicitExt) return explicitExt;

  const fromName = extractExtensionFromPath(uploadDetails.name);
  if (fromName) return fromName;

  const fromUrl = extractExtensionFromPath(uploadDetails.url);
  if (fromUrl) return fromUrl;

  if (uploadDetails.mimeType) {
    const mime = uploadDetails.mimeType.split(';')[0]?.trim().toLowerCase() ?? '';
    const fromMime = MIME_TO_EXTENSION[mime];
    if (fromMime) return fromMime;
  }

  return undefined;
}

/**
 * Extract the basename (final path segment) of a URL or path. Strips
 * query string + fragment first. Returns `undefined` when the value is
 * missing or yields an empty segment.
 *
 * Used by URL-input extractors as the `fileName` fallback when the
 * block content doesn't carry an explicit `uploadDetails.name`.
 */
export function extractFileNameFromPath(value?: string): string | undefined {
  if (!value) return undefined;
  const cleaned = value.split('?')[0]?.split('#')[0] ?? '';
  const lastSlash = cleaned.lastIndexOf('/');
  const basename = lastSlash === -1 ? cleaned : cleaned.slice(lastSlash + 1);
  return basename || undefined;
}

function toLowerCase(value?: string): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim().toLowerCase();
  return trimmed || undefined;
}
